// ====================================================
// src/components/block/liveStreamManagement/videoEffects/HandTracker.ts
//
// Suivi des mains en temps réel, pour l'effet "Avatar" (cf. AvatarEffect.tsx) —
// même patron que FaceTracker.ts : MediaPipe Tasks Vision (WASM), modèle "hand
// landmarker" hébergé par Google, chargé à la demande, singleton pour toute la
// session. Tâche MediaPipe DISTINCTE du visage/détourage — trois modèles, trois
// singletons indépendants, même si le runtime WASM vient du même package.
//
// Comme FaceLandmarker (et contrairement à ImageSegmenter.segmentForVideo,
// callback), HandLandmarker.detectForVideo() est SYNCHRONE — detectHandsInVideo()
// reste async uniquement pour attendre le chargement du modèle au premier appel.
// ====================================================

import { FilesetResolver, HandLandmarker, type HandLandmarkerResult } from '@mediapipe/tasks-vision';
import { isFaceDetectionDue, fitWithin } from './faceTrackingBudget';

const WASM_BASE = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.21/wasm';
const MODEL_URL = 'https://storage.googleapis.com/mediapipe-models/hand_landmarker/hand_landmarker/float16/latest/hand_landmarker.task';
// 2 mains × jusqu'à 2 personnes dans le cadre — même choix que FaceTracker (MAX_FACES).
const MAX_HANDS = 4;

let landmarkerPromise: Promise<HandLandmarker> | null = null;

async function createHandLandmarker(vision: Awaited<ReturnType<typeof FilesetResolver.forVisionTasks>>, delegate: 'GPU' | 'CPU'): Promise<HandLandmarker> {
  return HandLandmarker.createFromOptions(vision, {
    baseOptions: { modelAssetPath: MODEL_URL, delegate },
    runningMode: 'VIDEO',
    numHands: MAX_HANDS,
  });
}

function getHandLandmarker(): Promise<HandLandmarker> {
  if (!landmarkerPromise) {
    landmarkerPromise = FilesetResolver.forVisionTasks(WASM_BASE).then(async vision => {
      try {
        return await createHandLandmarker(vision, 'GPU');
      } catch (err) {
        console.warn('[HandTracker] délégué GPU indisponible, repli CPU :', err);
        return createHandLandmarker(vision, 'CPU');
      }
    });
  }
  return landmarkerPromise;
}

export interface HandLandmarkPoint { x: number; y: number; z: number; }

export interface HandTrackingResult {
  /** 21 points normalisés (0..1 dans le cadre de la frame), pour CETTE main —
   * cf. computeHandAnchorPoint dans AvatarRenderer.ts pour les indices utilisés. */
  landmarks: HandLandmarkPoint[];
  /** 'Left' ou 'Right' — repère MediaPipe, donc MIROIR de la caméra selfie (inversé
   * par rapport à la main réelle) : sur l'image NON retournée de la source, 'Right'
   * est la main GAUCHE de la personne. Utiliser isPersonLeftHand plutôt que de
   * comparer cette chaîne à la main. */
  handedness: string;
}

/** Vrai pour la main GAUCHE de la personne filmée (celle qui, face caméra, apparaît à
 * droite de l'image). MediaPipe suppose une image retournée en miroir (caméra selfie) ;
 * l'image de la source ne l'est pas, d'où l'inversion. UN SEUL endroit à corriger si le
 * côté s'avérait inversé à l'usage. */
export function isPersonLeftHand(hand: HandTrackingResult): boolean {
  return hand.handedness === 'Right';
}

/**
 * Détecte les mains (jusqu'à MAX_HANDS) dans une frame de `video` — tableau vide si
 * aucune. `timestampMs` doit être strictement croissant entre deux appels — même
 * contrainte que detectFaceInVideo (cf. FaceTracker) : un seul appelant à la fois.
 */
// Même budget de calcul que le suivi de visage (cf. faceTrackingBudget.ts) : cadence plafonnée,
// dernier résultat réutilisé par <video>, image réduite (repères normalisés → inchangés).
const lastByVideo = new WeakMap<HTMLVideoElement, { at: number; result: HandTrackingResult[] }>();
let downscaleCanvas: HTMLCanvasElement | null = null;

function detectorInput(video: HTMLVideoElement): HTMLVideoElement | HTMLCanvasElement {
  const { width, height } = fitWithin(video.videoWidth, video.videoHeight);
  if (width === video.videoWidth && height === video.videoHeight) return video;
  try {
    if (!downscaleCanvas) downscaleCanvas = document.createElement('canvas');
    if (downscaleCanvas.width !== width || downscaleCanvas.height !== height) {
      downscaleCanvas.width = width; downscaleCanvas.height = height;
    }
    const ctx = downscaleCanvas.getContext('2d');
    if (!ctx) return video;
    ctx.drawImage(video, 0, 0, width, height);
    return downscaleCanvas;
  } catch {
    return video;
  }
}

export async function detectHandsInVideo(video: HTMLVideoElement, timestampMs: number): Promise<HandTrackingResult[]> {
  if (video.readyState < 2 || video.videoWidth === 0) return [];
  const previous = lastByVideo.get(video);
  if (previous && !isFaceDetectionDue(timestampMs, previous.at)) return previous.result;
  let landmarker: HandLandmarker;
  try {
    landmarker = await getHandLandmarker();
  } catch (err) {
    console.warn('[HandTracker] échec de chargement du modèle :', err);
    return [];
  }

  try {
    const result: HandLandmarkerResult = landmarker.detectForVideo(detectorInput(video), timestampMs);
    if (!result.landmarks || result.landmarks.length === 0) { lastByVideo.set(video, { at: timestampMs, result: [] }); return []; }
    const hands = result.landmarks.map((landmarks, i) => ({
      landmarks: landmarks.map(p => ({ x: p.x, y: p.y, z: p.z })),
      handedness: result.handednesses?.[i]?.[0]?.categoryName ?? 'Unknown',
    }));
    lastByVideo.set(video, { at: timestampMs, result: hands });
    return hands;
  } catch (err) {
    console.warn('[HandTracker] échec de détection :', err);
    return [];
  }
}
